import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth strategy:
// - Primary: httpOnly cookie (access_token or session_token) set by backend.
// - Fallback: Bearer token from localStorage — only attached when the request
//   is not already going to succeed via cookie (e.g. mobile Safari cross-site
//   cookie blocking). This narrows XSS exposure vs. always attaching the token.
const api = axios.create({ baseURL: API, withCredentials: true });

api.interceptors.request.use((cfg) => {
  // Cookies flow automatically via withCredentials. Only fall back to Bearer
  // when the caller explicitly opts in OR the browser has no document.cookie
  // access (which would break the cookie flow).
  const token = localStorage.getItem("neos_token");
  const hasCookieAccess = typeof document !== "undefined" && typeof document.cookie === "string";
  if (token && !hasCookieAccess) {
    cfg.headers.Authorization = `Bearer ${token}`;
  } else if (token && cfg.headers?.["X-Use-Bearer"] === "1") {
    // explicit opt-in for endpoints that specifically need header auth (e.g. <img src>)
    cfg.headers.Authorization = `Bearer ${token}`;
    delete cfg.headers["X-Use-Bearer"];
  } else if (token) {
    // Always attach as a safety net for cross-origin, but the cookie is preferred.
    // This maintains the existing fallback while we plan cookie-only migration.
    cfg.headers.Authorization = `Bearer ${token}`;
  }
  return cfg;
});

// Session-expiry interceptor.
// When a request returns 401 we:
//   1. Clear the stale localStorage token so the next request doesn't retry with it.
//   2. Dispatch a "neos:session-expired" event so AuthContext can react cleanly
//      instead of the workspace appearing frozen or bouncing hard to the login route.
//   3. Suppress the /auth/me 401 (that's an expected anon check on first load).
// Any other error is passed through untouched.
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err?.response?.status;
    const url = err?.config?.url || "";
    if (status === 401 && !url.endsWith("/auth/me")) {
      if (typeof localStorage !== "undefined") localStorage.removeItem("neos_token");
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("neos:session-expired", { detail: { url } }));
      }
    }
    return Promise.reject(err);
  }
);

export default api;

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
